import { useMemo, useState } from 'react';
import type { MovementSegment } from '@geotracker/contracts';
import { cn } from '@/lib/utils';
import { formatClock, formatDistance, formatDuration, formatSpeed } from '@/lib/format';

/**
 * The day as one horizontal band: travelling, paused, stopped, or silent.
 *
 * This is the chart that answers "what did the day actually look like" in one
 * glance, which no table of totals does — "6h moving, 2h stopped" is the same
 * summary for a driver who did one long run and one who did fourteen drops.
 *
 * Width is time, so the band is a true timeline rather than a proportion bar:
 * a gap is as wide as the gap was. Colour carries the state, and because two of
 * the four states are deliberately hueless, the gap additionally carries a
 * hatch — the pair of neutrals has to stay separable for a reader who is
 * looking at the two of them side by side.
 */

const KIND_STYLE: Record<
  MovementSegment['kind'],
  { fill: string; label: string; legendSwatch: string }
> = {
  moving: { fill: 'fill-track-moving', label: 'Moving', legendSwatch: 'bg-track-moving' },
  stopped: { fill: 'fill-track-stopped', label: 'Stopped', legendSwatch: 'bg-track-stopped' },
  idle: { fill: 'fill-track-idle', label: 'Paused', legendSwatch: 'bg-track-idle' },
  gap: { fill: 'fill-track-gap', label: 'No data', legendSwatch: 'bg-track-gap' },
};

interface Props {
  segments: MovementSegment[];
  timezone?: string;
  /** Called with the segment's start time when a band is clicked. */
  onSeek?: (iso: string) => void;
  className?: string;
}

export function DayTimeline({ segments, timezone, onSeek, className }: Props) {
  const [hovered, setHovered] = useState<number | null>(null);

  const model = useMemo(() => {
    const first = segments[0];
    const final = segments[segments.length - 1];
    if (!first || !final) return null;

    const start = new Date(first.from).getTime();
    const end = new Date(final.to).getTime();
    const span = Math.max(1, end - start);

    const bands = segments.map((segment, i) => {
      const from = new Date(segment.from).getTime();
      const to = new Date(segment.to).getTime();
      return {
        segment,
        index: i,
        x: ((from - start) / span) * 100,
        // A floor, so a 40-second pause between two long drives is still a
        // visible tick rather than a sub-pixel sliver that cannot be hovered.
        width: Math.max(0.35, ((to - from) / span) * 100),
      };
    });

    return { start, end, span, bands };
  }, [segments]);

  if (!model) {
    return (
      <p className={cn('py-6 text-center text-sm text-muted-foreground', className)}>
        No movement recorded for this day.
      </p>
    );
  }

  const active = hovered === null ? null : model.bands[hovered];
  const present = new Set(segments.map((s) => s.kind));

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative">
        <svg
          viewBox="0 0 100 10"
          preserveAspectRatio="none"
          className="h-9 w-full overflow-visible"
          role="img"
          aria-label="Movement over the day"
        >
          <defs>
            {/* Missing data as a hatch rather than a colour: a gap is not a
                value, and the hatch is what keeps it distinct from the paused
                neutral for a reader who cannot rely on hue. */}
            <pattern
              id="gap-hatch"
              width="3"
              height="3"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="3" height="3" className="fill-muted" />
              <line x1="0" y1="0" x2="0" y2="3" className="stroke-track-gap" strokeWidth="1.1" />
            </pattern>
          </defs>

          {model.bands.map((band) => (
            <rect
              key={band.index}
              x={band.x}
              // 2px of surface between fills instead of a stroke around each.
              width={Math.max(0.2, band.width - 0.15)}
              y="0"
              height="10"
              rx="0.6"
              className={cn(
                band.segment.kind === 'gap' ? '' : KIND_STYLE[band.segment.kind].fill,
                onSeek && 'cursor-pointer',
                hovered !== null && hovered !== band.index && 'opacity-45',
                'transition-opacity',
              )}
              fill={band.segment.kind === 'gap' ? 'url(#gap-hatch)' : undefined}
              onMouseEnter={() => setHovered(band.index)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSeek?.(band.segment.from)}
            />
          ))}
        </svg>

        {/* Endpoints only. A tick every hour on a band this short is noise, and
            the tooltip carries the exact time for any point in between. */}
        <div className="mt-1 flex justify-between text-2xs tabular-nums text-muted-foreground">
          <span>{formatClock(segments[0]?.from, timezone)}</span>
          <span>{formatClock(segments[segments.length - 1]?.to, timezone)}</span>
        </div>

        {active ? (
          <div
            role="tooltip"
            className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg border border-border bg-card px-2.5 py-1.5 text-2xs shadow-lg"
            style={{ left: `${Math.min(92, Math.max(8, active.x + active.width / 2))}%` }}
          >
            <div className="font-semibold">{KIND_STYLE[active.segment.kind].label}</div>
            <div className="mt-0.5 tabular-nums text-muted-foreground">
              {formatClock(active.segment.from, timezone)} –{' '}
              {formatClock(active.segment.to, timezone)} · {formatDuration(active.segment.seconds)}
            </div>
            {active.segment.kind === 'moving' ? (
              <div className="tabular-nums text-muted-foreground">
                {formatDistance(active.segment.distanceMeters)} ·{' '}
                {formatSpeed(active.segment.avgSpeedMps)} avg
                {active.segment.compass ? ` · ${active.segment.compass}` : ''}
              </div>
            ) : null}
            {active.segment.kind === 'gap' ? (
              <div className="text-muted-foreground">Device reported nothing</div>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-muted-foreground">
        {(['moving', 'stopped', 'idle', 'gap'] as const)
          .filter((kind) => present.has(kind))
          .map((kind) => (
            <li key={kind} className="flex items-center gap-1.5">
              <span
                className={cn(
                  'inline-block h-2 w-4 rounded-sm',
                  kind === 'gap'
                    ? 'border border-track-gap/60 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,hsl(var(--track-gap))_2px,hsl(var(--track-gap))_3px)]'
                    : KIND_STYLE[kind].legendSwatch,
                )}
              />
              {KIND_STYLE[kind].label}
            </li>
          ))}
      </ul>
    </div>
  );
}
